import { QueryKey } from "@tanstack/react-query";

import { sortObject } from "@/utils/object";
import { CONVERSATION_LIST_CACHE_KEY } from "@/features/inbox/api/keys";
import {
  hasPendingDraft,
  pendingApprovalCount,
  pendingQuestionCount,
} from "@/features/contact/helpers/ai-pending";
import { ASSIGNED_TO_TEAMMATES } from "@/features/inbox/helpers/inbox-filter-url";
import { InboxFilter } from "@/features/inbox/stores/inbox";
import { UploadAttachment } from "@/types/attachment-upload";
import { CreateConversationMessagePayloadsInput } from "@/features/inbox/types/conversation";
import { Conversation } from "@/types/conversation";
import { SORT_OPTION, InboxSortKey } from "@/features/inbox/types/inbox";
import {
  ATTACHMENT_TYPE,
  AttachmentType,
  OWNER,
  SEND_EVENT_MESSAGE_TYPE,
  SendEventMessageType,
  SendMessagePayload,
} from "@/types/message";

export const getMessageTypeFromFile = (file: File): SendEventMessageType => {
  const type = file.type;

  if (type.startsWith("image")) {
    return SEND_EVENT_MESSAGE_TYPE.IMAGE;
  }
  if (type.startsWith("video")) {
    return SEND_EVENT_MESSAGE_TYPE.VIDEO;
  }
  if (type.startsWith("audio")) {
    return SEND_EVENT_MESSAGE_TYPE.AUDIO;
  }

  return SEND_EVENT_MESSAGE_TYPE.FILE;
};

const SEND_MESSAGE_TYPE_BY_ATTACHMENT_TYPE: Record<
  AttachmentType,
  SendEventMessageType
> = {
  [ATTACHMENT_TYPE.IMAGE]: SEND_EVENT_MESSAGE_TYPE.IMAGE,
  [ATTACHMENT_TYPE.VIDEO]: SEND_EVENT_MESSAGE_TYPE.VIDEO,
  [ATTACHMENT_TYPE.AUDIO]: SEND_EVENT_MESSAGE_TYPE.AUDIO,
  [ATTACHMENT_TYPE.FILE]: SEND_EVENT_MESSAGE_TYPE.FILE,
  [ATTACHMENT_TYPE.IFRAME]: SEND_EVENT_MESSAGE_TYPE.FILE,
  // A sticker is an image when re-sent outbound.
  [ATTACHMENT_TYPE.STICKER]: SEND_EVENT_MESSAGE_TYPE.IMAGE,
};

/**
 * Resolve the send type for an attachment. Prefer the real File's MIME when we
 * have it, but fall back to the stored attachment type so url-only attachments
 * (e.g. snippet assets added without re-downloading the File) still send.
 */
export const getSendMessageTypeFromAttachment = (
  attachment: UploadAttachment,
): SendEventMessageType =>
  attachment.file
    ? getMessageTypeFromFile(attachment.file)
    : (SEND_MESSAGE_TYPE_BY_ATTACHMENT_TYPE[attachment.type] ??
      SEND_EVENT_MESSAGE_TYPE.FILE);

export const createConversationMessagePayloads = ({
  contactId,
  channelId,
  text,
  attachments,
  replyToMessageId,
  choices,
  isAiAssisted,
}: CreateConversationMessagePayloadsInput): Array<SendMessagePayload> => {
  // we don't send caption attachment as of now, just for simplicity
  let allAttachments = [];

  if (text) {
    // With choices the text goes out as a quick reply; each channel renders it
    // as buttons or a list/menu depending on how many choices there are.
    const hasChoices = Boolean(choices?.length);

    allAttachments.push({
      contactId,
      channelId,
      type: hasChoices
        ? SEND_EVENT_MESSAGE_TYPE.QUICK_REPLY
        : SEND_EVENT_MESSAGE_TYPE.TEXT,
      text,
      ...(isAiAssisted && { isAiAssisted: true }),
      ...(hasChoices && {
        quickReply: {
          text,
          buttons: choices!.map((choice) => ({
            text: choice,
            payload: choice,
          })),
        },
      }),
      ...(replyToMessageId ? { replyTo: replyToMessageId } : {}),
    });
  }

  attachments?.forEach((attachment) => {
    const type = getSendMessageTypeFromAttachment(attachment);

    allAttachments.push({
      contactId,
      channelId,
      type: type,
      [type]: {
        url: attachment.url,
        name: attachment.name ?? attachment.file?.name,
        contentType: attachment.file?.type,
      },
    });
  });

  return allAttachments;
};

/**
 * Since when the contact has been waiting on us - null once anyone replied, so
 * answered conversations sort to the tail. Mirrors the backend's
 * WAITING_SINCE_EXPRESSION.
 */
export const waitingSinceMs = (
  contact: Conversation["contact"],
): number | null =>
  contact.lastMessageOwner === OWNER.CONTACT && contact.lastMessageProcessedAt
    ? new Date(contact.lastMessageProcessedAt).getTime()
    : null;

/** Mirrors the backend's ECHO_PRIORITY_EXPRESSION: a question Echo is parked
 *  on, then an action to approve, then a draft to review, then everything
 *  else. */
export const echoPriority = (contact: Conversation["contact"]): number => {
  if (pendingQuestionCount(contact) > 0) return 0;
  if (pendingApprovalCount(contact) > 0) return 1;

  return hasPendingDraft(contact) ? 2 : 3;
};

export const sortConversations = (
  conversations: Conversation[],
  sortBy: InboxSortKey,
) => {
  // Copy before sorting: callers pass arrays that live in the react-query
  // cache, and Array.prototype.sort mutates in place.
  const sorted = [...conversations];

  if (
    sortBy === SORT_OPTION.WAITING_OLDEST ||
    sortBy === SORT_OPTION.WAITING_NEWEST
  ) {
    const direction = sortBy === SORT_OPTION.WAITING_OLDEST ? 1 : -1;

    return sorted.sort((a, b) => {
      const waitingA = waitingSinceMs(a.contact);
      const waitingB = waitingSinceMs(b.contact);

      if (waitingA === null && waitingB === null) return 0;
      if (waitingA === null) return 1;
      if (waitingB === null) return -1;

      return (waitingA - waitingB) * direction;
    });
  }

  // Within a group, the longest-waiting customer first; answered ones last.
  if (sortBy === SORT_OPTION.PRIORITY) {
    return sorted.sort((a, b) => {
      const byGroup = echoPriority(a.contact) - echoPriority(b.contact);

      if (byGroup !== 0) return byGroup;
      const waitingA = waitingSinceMs(a.contact) ?? Number.POSITIVE_INFINITY;
      const waitingB = waitingSinceMs(b.contact) ?? Number.POSITIVE_INFINITY;

      return waitingA === waitingB ? 0 : waitingA < waitingB ? -1 : 1;
    });
  }

  if (sortBy === SORT_OPTION.DUE_SOONEST) {
    return sorted.sort((a, b) => {
      const da = a.contact.followUp?.dueAt;
      const db = b.contact.followUp?.dueAt;

      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;

      return new Date(da).getTime() - new Date(db).getTime();
    });
  }

  if (sortBy === SORT_OPTION.NEWEST) {
    return sorted.sort(
      (conversationA, conversationB) =>
        new Date(conversationB.contact.lastBumpedAt).getTime() -
        new Date(conversationA.contact.lastBumpedAt).getTime(),
    );
  }

  return sorted.sort(
    (conversationA, conversationB) =>
      new Date(conversationA.contact.lastBumpedAt).getTime() -
      new Date(conversationB.contact.lastBumpedAt).getTime(),
  );
};

export const createConversationCacheKey = (filter: InboxFilter): QueryKey => {
  const sortedFilter = sortObject(filter);

  return [...CONVERSATION_LIST_CACHE_KEY, { ...sortedFilter }];
};

/**
 * Build the JSON body for `POST /conversations/search`. The advanced filter is
 * a nested AND/OR group, so it travels as a body (not a query string); status,
 * sort, search and assignment stay as plain conversation-level fields.
 *
 * An active search takes precedence over everything else: when there's a search
 * term it scopes the list on its own and every other filter - the
 * mine/unassigned/all assignment tab, the view, the status and the advanced
 * filter query - is intentionally dropped, so a search always matches across
 * the whole workspace regardless of the filters that were set before it.
 */
export const makeConversationSearchBody = (
  filter: InboxFilter,
  cursor?: string,
): Record<string, unknown> => {
  const body: Record<string, unknown> = {
    limit: 25,
    sortBy: filter.sort,
  };

  if (cursor) {
    body.next = cursor;
  }

  if (filter.search.term.length > 0) {
    body.search = filter.search.term;
    body.searchFor = filter.search.$for;

    return body;
  }

  if (filter.assignedTo === ASSIGNED_TO_TEAMMATES) {
    // "Anyone but me" has no representation in `assignedTo`, which holds a single
    // id; the backend reads it off its own flag and relies on NULL <> uuid being
    // NULL to leave unassigned work out.
    body.assignedToOthers = true;
  } else if (filter.assignedTo !== "all") {
    // The "Unassigned" tab is the literal string "null": the backend matches
    // unassigned conversations on `assignedTo === 'null'` (a JSON null would
    // fall through every branch and return all conversations).
    body.assignedTo = filter.assignedTo === null ? "null" : filter.assignedTo;
  }

  if (filter.view !== "all") body.view = filter.view;

  if (filter.status && filter.status !== "all") {
    body.status = filter.status;
  }

  if (filter.query && filter.query.rules.length > 0) {
    body.filter = filter.query;
  }

  return body;
};
